import { useEffect, useState } from "react";
import { useAutoOpen } from "@/hooks/useAutoOpen";
import { api, formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/app/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2, Pencil } from "lucide-react";
import Pagination, { usePagination } from "@/components/app/Pagination";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SUMBER_LIST, sumberOf } from "@/components/app/bakar/sumber";
import { usePabrik } from "@/context/PabrikContext";
import { useAuth } from "@/context/AuthContext";
import { PajakDefaultFields } from "@/components/app/PajakDefaultFields";

const SUMBER_JENIS = { tungku: ["tungku", "kulitan"], reseller: ["beli"], briket: ["briket"] };
const useSumberList = () => {
  const { enabled, current } = usePabrik();
  const allow = enabled ? SUMBER_JENIS[current?.jenis] : null;
  return allow ? SUMBER_LIST.filter((s) => allow.includes(s.id)) : SUMBER_LIST;
};

const SumberSelect = ({ value, onChange, testid }) => {
  const list = useSumberList();
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="w-full sm:w-56" data-testid={testid}><SelectValue placeholder="Sumber produk"/></SelectTrigger>
      <SelectContent>{list.map((s) => <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>)}</SelectContent>
    </Select>
  );
};

export default function KategoriPage({ tipe, title, subtitle, testidPrefix }) {
  const [items, setItems] = useState([]);
  const [nama, setNama] = useState("");
  const sumberList = useSumberList();
  const [sumber, setSumber] = useState(sumberList[0]?.id || "tungku");
  const isProduk = tipe === "produk";
  const { user: authUser } = useAuth();
  const pajakRule = tipe === "pemasukan" && authUser?.workspace_type === "pribadi";
  const [selected, setSelected] = useState(new Set());
  const [editing, setEditing] = useState(null); // {id, nama}

  const load = async () => { const r = await api.get("/kategori", { params: { tipe } }); setItems(r.data); };
  useEffect(() => { setSelected(new Set()); load(); }, [tipe]);

  useAutoOpen(() => setTimeout(() => document.querySelector(`[data-testid="${testidPrefix}-input"]`)?.focus(), 150));
  const add = async () => {
    if (!nama.trim()) return;
    try { await api.post("/kategori", { nama: nama.trim(), tipe, ...(isProduk ? { sumber } : {}) }); setNama(""); await load(); toast.success("Kategori ditambahkan"); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const delOne = async (id) => {
    if (!confirm("Hapus kategori ini?")) return;
    try { await api.delete(`/kategori/${id}`); await load(); toast.success("Terhapus"); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const toggle = (id) => { const s=new Set(selected); s.has(id)?s.delete(id):s.add(id); setSelected(s); };
  const bulkDel = async () => {
    if (!selected.size || !confirm(`Hapus ${selected.size} kategori?`)) return;
    try {
      await api.post("/kategori/bulk-delete", { ids: Array.from(selected) });
      setSelected(new Set()); await load(); toast.success("Terhapus");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const saveEdit = async () => {
    if (!editing?.nama.trim()) return;
    try {
      await api.put(`/kategori/${editing.id}`, { nama: editing.nama.trim(), ...(isProduk ? { sumber: editing.sumber } : {}),
        ...(pajakRule ? { pajak_jenis: editing.pajak_jenis || null, pajak_perlakuan: editing.pajak_perlakuan || null, pajak_tarif: editing.pajak_tarif ?? null } : {}) });
      setEditing(null); await load(); toast.success("Kategori diperbarui");
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const { page, setPage, totalPages, paged, total, pageSize } = usePagination(items, 10);

  return (
    <>
      <PageHeader title={title} subtitle={subtitle} actions={
        selected.size > 0 ? <Button variant="outline" onClick={bulkDel} data-testid={`${testidPrefix}-bulk`} className="rounded-sm"><Trash2 size={14} className="mr-1"/> Hapus ({selected.size})</Button> : null
      }/>
      <div className="p-4 md:p-8 space-y-6">
        <div className="card-flat p-4 md:p-5 max-w-xl">
          <div className="flex flex-col sm:flex-row gap-2">
            <Input placeholder="Nama kategori baru" value={nama} onChange={(e)=>setNama(e.target.value)}
              onKeyDown={(e)=>e.key==="Enter"&&add()} data-testid={`${testidPrefix}-input`}/>
            {isProduk && <SumberSelect value={sumber} onChange={setSumber} testid={`${testidPrefix}-sumber`}/>}
            <Button onClick={add} className="brand-btn rounded-sm" data-testid={`${testidPrefix}-add`}>
              <Plus size={14} className="mr-1"/> Tambah
            </Button>
          </div>
        </div>
        <div className="card-flat overflow-hidden">
          <table className="w-full text-sm">
            <thead><tr className="bg-slate-50 border-b border-slate-200 text-left text-[12px] uppercase tracking-wide text-slate-500">
              <th className="p-3 w-10"></th><th className="p-3">Nama Kategori</th>{isProduk && <th className="p-3">Sumber</th>}<th className="p-3 text-right">Aksi</th>
            </tr></thead>
            <tbody>
              {items.length===0 && <tr><td colSpan={isProduk ? 4 : 3} className="text-center py-10 text-slate-400">Belum ada kategori.</td></tr>}
              {paged.map(k=>(
                <tr key={k.id} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="p-3"><Checkbox checked={selected.has(k.id)} onCheckedChange={()=>toggle(k.id)}/></td>
                  <td className="p-3">{k.nama}{k.is_default && <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded-sm bg-blue-50 text-blue-700" title="Data lama tanpa produk otomatis masuk ke produk ini">Default</span>}</td>
                  {isProduk && <td className="p-3"><span className={`text-[11px] px-2 py-0.5 rounded-sm border whitespace-nowrap ${sumberOf(k.sumber).cls}`} data-testid={`${testidPrefix}-sumber-${k.id}`}>{sumberOf(k.sumber).label}</span></td>}
                  <td className="p-3">
                    <div className="flex justify-end gap-1">
                      <button onClick={()=>setEditing({id:k.id, nama:k.nama, sumber:k.sumber || "tungku", pajak_jenis:k.pajak_jenis, pajak_perlakuan:k.pajak_perlakuan, pajak_tarif:k.pajak_tarif})} data-testid={`${testidPrefix}-edit-${k.id}`} className="p-1.5 rounded-sm hover:bg-slate-100"><Pencil size={14}/></button>
                      <button onClick={()=>delOne(k.id)} className="p-1.5 rounded-sm hover:bg-rose-50"><Trash2 size={14} className="text-rose-600"/></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <Pagination page={page} setPage={setPage} totalPages={totalPages} total={total} pageSize={pageSize} testid={`${testidPrefix}-pagination`}/>
        </div>
      </div>
      <Dialog open={!!editing} onOpenChange={(v)=>!v && setEditing(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Ubah Kategori</DialogTitle></DialogHeader>
          <Input value={editing?.nama || ""} onChange={(e)=>setEditing({...editing, nama:e.target.value})} data-testid={`${testidPrefix}-edit-nama`}/>
          {isProduk && <div className="space-y-1"><div className="text-xs text-slate-500">Sumber produk (menentukan cara stok masuk)</div><SumberSelect value={editing?.sumber || "tungku"} onChange={(v)=>setEditing({...editing, sumber:v})} testid={`${testidPrefix}-edit-sumber`}/></div>}
          {pajakRule && <PajakDefaultFields value={editing} onChange={setEditing}/>}
          <DialogFooter>
            <Button variant="outline" onClick={()=>setEditing(null)} className="rounded-sm">Batal</Button>
            <Button onClick={saveEdit} className="brand-btn rounded-sm" data-testid={`${testidPrefix}-edit-save`}>Simpan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
